import React from 'react';
import { BootstrapResource, CadFeatureMap } from '@mark43/cad-api';
import { EnabledFeaturesContext } from './enabled-features-context';
import { useQuery } from '@tanstack/react-query';
import { RefreshEnabledFeaturesContext } from './refresh-enabled-features-context';
import { getDevAllEnabledFeatures, metricTagsToHeaders } from '@m43/core';
import { setUseFetchClient, useApiClient, useApiClientModeSetter } from '~/api-client';
import { stableQueryKeys } from '../../helpers/stable-query-keys';
import { BootstrapErrorContainer, BootstrapLoadingContainer } from '../../../bootstrap/bootstrap-provider';

export type EnabledFeaturesBroadcast = {
    setEnabledFeaturesRef: React.MutableRefObject<
        ((value: React.SetStateAction<CadFeatureMap | undefined>) => void) | undefined
    >;
    postRefreshEnabledFeaturesMessage: (enabledFeatures: CadFeatureMap) => void;
};

export function EnabledFeaturesProvider({
    children,
    enabledFeaturesBroadcast,
    enabledFeaturesBootstrap,
}: {
    children: React.ReactElement;
    enabledFeaturesBroadcast?: EnabledFeaturesBroadcast;
    enabledFeaturesBootstrap?: CadFeatureMap;
}): React.ReactElement {
    const apiClient = useApiClient();
    const editApiClient = useApiClientModeSetter();
    const [enabledFeatures, setEnabledFeatures] = React.useState<CadFeatureMap | undefined>(
        enabledFeaturesBootstrap
    );

    if (enabledFeaturesBroadcast) {
        const { setEnabledFeaturesRef } = enabledFeaturesBroadcast;
        if (!setEnabledFeaturesRef.current) {
            setEnabledFeaturesRef.current = setEnabledFeatures;
        }
    }

    const { isError, isInitialLoading, refetch } = useQuery({
        queryKey: stableQueryKeys.ENABLED_FEATURES_QUERY_KEY,
        queryFn() {
            return apiClient.request<BootstrapResource.GetCadFeatureMap>({
                method: 'GET',
                url: 'bootstrap/feature_map/',
            });
        },
        onSuccess(response) {
            const allDevEnabledFeatures = getDevAllEnabledFeatures();
            const enabledFeatures =
                allDevEnabledFeatures !== undefined
                    ? { ...response.data.data, ...allDevEnabledFeatures }
                    : response.data.data;
            if (enabledFeatures) {
                setUseFetchClient(enabledFeatures.feUseAxiosFetch);
                enabledFeaturesBroadcast?.postRefreshEnabledFeaturesMessage(enabledFeatures);
                setEnabledFeatures(enabledFeatures);
                if (!window.Cypress) {
                    editApiClient.addHeader({
                        'x-cad-subpremise-support-enabled': enabledFeatures.subpremiseSupport.toString(),
                        ...metricTagsToHeaders(),
                    });
                }
            }
        },
        enabled: !enabledFeaturesBootstrap,
    });

    if (!enabledFeatures) {
        if (isInitialLoading) {
            return (
                <BootstrapLoadingContainer />
            );
        }
        if (isError) {
            return (
                <BootstrapErrorContainer onRetry={() => refetch()} />
            );
        }
    }

    return (
        <EnabledFeaturesContext.Provider value={enabledFeatures}>
            <RefreshEnabledFeaturesContext.Provider value={refetch}>
                {children}
            </RefreshEnabledFeaturesContext.Provider>
        </EnabledFeaturesContext.Provider>
    );
}
